/**
 * API Types — hand-maintained mirrors of the Go wire types in
 * api-service-go/types.go. The backend is authoritative: its OpenAPI spec
 * is specs/api/api-service.yaml (served live at GET /api/v1/docs). When a
 * discrepancy exists, the backend wins — update this file to match.
 *
 * Frontend-only types (domain models, SSE streaming events, etc.) are also
 * defined directly in this file.
 */

// House-standard resource pointer (location/role/org). Re-exported so consumers
// keep importing wire types from "@/api/types".
export type { Reference } from '@/lib/reference';
import type { Reference } from '@/lib/reference';

// ============================================================================
// Backend Wire Types (mirrors of api-service-go/types.go)
// ============================================================================
// Previously aliases into a generated-types.ts fossil (dead FastAPI spec);
// now materialized inline. Consuming code continues to import from
// "@/api/types" without changes.

// -- The list envelope --
//
// Every list endpoint answers `{"items": [...]}` (`apicontract.Page[T]`).
// `items` is never null and never absent — an empty collection is the answer
// `[]`, not a missing report. Whether the list continues is not in the body:
// the response carries `Link: <...>; rel="next"` (RFC 8288) when a row exists
// past the page, and `api.getPage` reads that header into `next`, the path of
// the next page, or null when this page is the last.
export type Page<T> = {
  items: T[];
  next: string | null;
};

/**
 * Rebuild a page around new rows, keeping where the list continues.
 *
 * Local edits to a cached page — an optimistic message, a streamed token, a
 * rolled-back placeholder — change the ROWS in hand and nothing about the
 * server's window, so `next` must survive them.
 */
export function withItems<T>(page: Page<T>, items: T[]): Page<T> {
  return { ...page, items };
}

// -- Auth --
export type LoginRequest = {
  email: string;
  password: string;
};
export type RegisterRequest = {
  email: string;
  password: string;
  name?: string | null;
  years_experience?: number | null;
  specialisation?: string | null;
  institution?: string | null;
  /** @default "de" */
  language: string;
  /** @default "Ava" */
  twin_name: string;
};
export type AuthResponse = {
  access_token: string;
  /** @default "bearer" */
  token_type: string;
  user: UserResponse;
  /**
   * READ-ONLY admin tier flag. Sent at the TOP LEVEL of AuthResponse (sibling
   * of `user`), NOT inside the nested UserResponse. Optional + omitempty: absent
   * on the wire means false. Gates the Admin section + aggregate tabs.
   */
  admin_view?: boolean;
  /**
   * ISO-8601 expiry of the 30-day new-user admin-view trial. At top level of
   * AuthResponse (sibling of `user`). Present only while inside the trial.
   */
  admin_view_trial_expires_at?: string;
  /**
   * The signing-in practitioner's OPEN onboarding module run, or null when
   * there is none. Top level, sibling of `user`, for the same reason
   * admin_view is: it decides where this sign-in lands, and a client that had
   * to ask afterwards would render a home board first and bounce a brand-new
   * nurse off it.
   */
};
// POST /auth/verify-email — raw token from the email link (the page reads it
// from `?token=`). Mirrors api-service-go VerifyEmailRequest.
export type VerifyEmailRequest = {
  token: string;
};
// POST /auth/resend-verification — generic by design (no account enumeration).
// Mirrors api-service-go ResendVerificationRequest.
export type ResendVerificationRequest = {
  email: string;
};
// 200 body POST /auth/verify-email returns when the token had ALREADY been
// consumed by this same account (a client-side double-submit retry, or a
// mail-gateway link-prefetch racing the real click) — see
// AuthService.VerifyEmail's replay handling. Deliberately carries NO
// access_token: re-minting a session from a token that may sit in a mail
// archive or gateway log forever would turn a one-time link into a standing,
// replayable credential. The account genuinely is verified; the caller just
// isn't logged in from THIS response.
export type AlreadyVerifiedResponse = {
  status: "already_verified";
};
// Discriminated result of authApi.verifyEmail, mirroring RegisterResult's
// `verificationSent` pattern above. `verified: true` (fresh consumption)
// carries the session the user was just logged into; `verified: false`
// (replay) carries nothing to log in with.
export type VerifyEmailResult =
  | { verified: true; auth: AuthResponse }
  | { verified: false };
// 202 body the register endpoint returns when verification is required.
export type VerificationSentResponse = {
  status: "verification_sent";
  email: string;
};
// Discriminated result of authApi.register. `verificationSent` lets the
// Register page branch between "show the inbox panel" (verification on) and
// "log in + go to onboarding" (verification off) without inspecting the HTTP
// status, which the api client does not expose.
export type RegisterResult =
  | { verificationSent: true; email: string }
  | { verificationSent: false; auth: AuthResponse };
export type TokenResponse = {
  access_token: string;
  /** @default "bearer" */
  token_type: string;
};

/**
 * POST /auth/password — the invitation link's consumer. The token proves the
 * address; the password is set and `activated` stamped in the same
 * transaction, and a FRESH consumption answers the session (same shape and
 * cookies as login). The password field is optional in the type only because
 * the API also accepts a token-only call to peek a link's state — the
 * set-password page always sends both.
 */
export type SetPasswordRequest = {
  token: string;
  password: string;
};

// -- OAuth 2.1 AS consent (MCP connectors) --
export type OAuthClientInfo = {
  client_id: string;
  client_name: string;
};
/** The authorize parameters the SPA received on the /oauth/authorize bounce,
 * plus the user's decision. Mirrors oauth_as.go authorizeDecisionRequest. */
export type OAuthAuthorizeDecisionRequest = {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  code_challenge: string;
  code_challenge_method: string;
  resource: string;
  scope: string;
  state: string;
  action: "approve" | "deny";
};
export type OAuthAuthorizeDecisionResponse = {
  redirect_url: string;
};

// -- User --

/** Profile fields are optional to support users who haven't completed onboarding. */
export type UserResponse = {
  id: string;
  email: string;
  name?: string | null;
  role?: string | null;
  years_experience?: number | null;
  institution?: string | null;
  unit?: string | null;
  language: string;
  specialisation?: string | null;
  twin_name: string;
  /** The practitioner's own summary of their practice, or null when they have
   *  written none. Nothing generates it: adr/0025 removed the AI-written
   *  profile summary and this attribute has exactly one writer, its owner.
   *  Optional for the same reason activated_at below is: a response from the
   *  previous image during a rollout does not carry the key at all, and a
   *  required field would fail the parse of the caller's whole profile — every
   *  screen, not only the narrative — while the two images overlap. Absent is
   *  "this response does not say"; null is "she has written none". */
  narrative?: string | null;
  /**
   * The caller's OPEN onboarding module run, or null when there is none.
   *
   * Optional AND nullable, and the two differ: null is the answer that sends
   * her to her home board, absent means this response does not carry the fact
   * (PUT /me answers the profile half of a user and says nothing about her
   * run). A gate that treats absent as null drops a brand-new nurse past her
   * own interview.
   */
  /**
   * When the account became usable — the verification click, the registration
   * itself where verification is off, or an identity provider vouching. null
   * means not activated, which is the state the login gate refuses. Optional
   * because a response from the previous image during a rollout does not carry
   * the key, and absent is "this response does not say".
   */
  activated_at?: string | null;
  /**
   * House-standard FHIR-style References (source of truth for where the user
   * works). `role`/`unit` above are the derived free-text display mirror.
   */
  role_ref?: Reference | null;
  location_ref?: Reference | null;
  /**
   * The deployment's org-tree root, present only when the deployment has one
   * unambiguous org root. `display` is the CURRENT org name — reading it
   * instead of the free-text `institution` snapshot below is what keeps the
   * profile screen from showing a stale name after the org root is renamed
   * (see adr/, the org-console rename bug this field fixes). Same plain
   * profile-field treatment as role_ref/location_ref above, not a
   * caller-capability (it reflects deployment state, not an ACL grant).
   */
  organisation_ref?: Reference | null;
  /**
   * True when this user holds the platform-admin role. Drives the Admin
   * Dashboard gating (raw-data tabs vs aggregate-only view). Optional on the
   * wire: older backends omit it, and absence must read as `false`.
   */
  is_platform_admin?: boolean;
  /**
   * READ-ONLY admin tier. True for full admins, delegated read-only admins, AND
   * every new user for their first 30 days. Gates the Admin SECTION (nav link +
   * aggregate dashboard tabs) — NOT the raw cross-user tabs or permission
   * management, which stay gated on `is_platform_admin`. Optional + absence
   * reads as `false`.
   */
  admin_view?: boolean;
  /**
   * ISO-8601 expiry of the 30-day new-user admin-view trial. Present only while
   * a user is currently inside that trial; absent for full/delegated admins and
   * for users whose trial has lapsed.
   */
  admin_view_trial_expires_at?: string;
  /**
   * Delegable approval authority (`system:platform#approver`). Gates the
   * Approvals admin area, which is deliberately NOT an `endpoint:` instance —
   * approval has its own relation, and a second mechanism for one decision
   * would be a duplicate. Absence reads as `false`.
   */
  can_approve?: boolean;
  /**
   * The admin areas this caller may open: the `endpoint:<id>` instances they
   * hold viewer on (a full admin reaches all of them via the model's
   * owner-from-system backstop). The dashboard renders its tab strip from this,
   * so a delegated grant is visible to the person it was granted to — before
   * #2409 the strip was drawn from `is_platform_admin` alone, which made
   * per-endpoint delegation enforced-but-invisible. Absence reads as `[]`.
   */
  admin_endpoints?: string[];
  /**
   * Every agent slug this caller may open a session with — the open catalogue
   * plus the restricted agents they hold `agent:<slug> viewer` on. The WHOLE
   * answer, resolved server-side, so no client re-derives an access rule the
   * server would then enforce differently. Absence reads as `[]`.
   */
  launchable_agents?: string[];
  /**
   * The caller's direct ward grants (admin-granted membership), independent
   * of `location_ref` (the user's own self-picked/onboarding unit). Same
   * caller-capability treatment as `admin_endpoints`/`launchable_agents`
   * above — ACL-derived, computed per /me call, and deliberately EXCLUDED
   * from the naive patch spread (see CALLER_CAPABILITY_KEYS) so a PUT /me
   * response that doesn't carry it cannot wipe out a
   * membership list GET /me already resolved. Absence reads as "not stated",
   * not "no memberships" — see toUser vs toUserPatch below.
   */
  location_memberships?: LocationMembership[];
  created_at: string;
  updated_at: string;
};
/** A caller's direct (admin-granted) ward membership — see location_memberships above. */
export type LocationMembership = {
  location_id: string;
  name: string;
  kind: string;
  role: string;
};
// PUT /me body. Email is intentionally absent — it is immutable
// post-registration, so the profile editor cannot change it. The role and the
// home unit are submitted as `role_ref`/`location_ref`, internal References the
// backend resolves to the rows they name; the response's `role` and `unit` are
// read from those rows and no request writes them. Each accepted change is
// recorded as a new version in the user-record event log.
export type UpdateProfileRequest = {
  name?: string | null;
  years_experience?: number | null;
  specialisation?: string | null;
  institution?: string | null;
  language?: string | null;
  twin_name?: string | null;
  /** Your own summary of your practice. Send null to clear it; OMIT it to
   *  leave it unchanged. The three are different writes and the backend keeps
   *  them apart, so a form must not collapse null and absent. */
  narrative?: string | null;
  /** The role and the home unit, as internal References ("Role/<id>",
   *  "Location/<uuid>") or the cleared sentinel. Anything else is refused 400
   *  INVALID_REFERENCE naming the field. Absent = leave unchanged. */
  role_ref?: Reference;
  location_ref?: Reference;
};
// -- Session --
export type CreateSessionRequest = {
  title?: string | null;
};
export type UpdateSessionRequest = {
  title: string;
};
export type SessionResponse = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  tags?: string[];
  /** Enriched tag information with labels (included when enrich_tags=true). */
  tag_info?: TagInfoResponse[] | null;
  /** When session was archived (null if not archived). */
  archived_at?: string | null;
  /** @default "pending" */
  is_shared?: boolean;
  agent_slug: string;
  /**
   * The session's assistant as a Reference. `reference` ("Agent/<slug>") is
   * present only while that agent still exists; once it is retired the server
   * sends `{display: "<slug>"}` alone and the session keeps working on the
   * default agent. Use `isRetiredAgent(session)` rather than testing fields by
   * hand.
   */
  agent?: Reference;
  /**
   * Server-derived classification: "conversation" | "training_run" |
   * "curriculum_run". A client cannot tell them apart — titles and message
   * counts are identical and the internal state never crosses the wire — so
   * filter with `GET /sessions?kind=` rather than guessing.
   */
  session_kind?: string;
  /** What this session is a session OF, when it is of something:
   *  `module:<id>@<version>`, `curriculum:<id>`, or a consult's continuation
   *  handle. Absent when the session is of nothing. */
  resource_key?: string;
  /**
   * Present iff this session's agent has `speaks_first` configured (e.g. the
   * twin's welcome session, the onboarding interview): the id of the
   * auto-enqueued opening turn (same hidden-note mechanism as the welcome
   * turn). Register this in usePendingTurnsStore right after session
   * creation so a turn the client never itself POSTed still gets the normal
   * typing-indicator + failure-toast + reload-catch-up coverage. Absent for
   * every ordinary session.
   */
  opening_turn_id?: string;
  state?: unknown;
  /**
   * Set when this session was spawned as a child of another (specialist
   * hand-off, e.g. the compatibility/yc agent invoked from a main chat).
   * Drives the "Back to chat" affordance on child sessions. Source-of-truth:
   * `SessionResponseSchema` in api/schemas.ts + the api-service-go session row.
   */
  parent_session_id?: string | null;
  /**
   * Denormalized assigned-patient id (null = personal/unassigned). Drives
   * patient-grouping in the handover workspace. Source-of-truth: the OpenFGA
   * (session,patient,patient) edge; this column mirrors it for cheap listing.
   */
  patient_id?: string | null;
  /** Creator display name for the cross-nurse "von <colleague>" byline (the
   *  creator id is already on the session as author_id). */
  author_name?: string | null;
  /** Creator (planner) user id. `author_id === current user` ⇒ this user owns
   *  the session and may edit/chat; otherwise it was shared TO them (read-only,
   *  enforced server-side by the sessionEditor gate on POST messages). Optional:
   *  older backends may omit it — consumers degrade to treating the viewer as
   *  the author (the server gate remains the real boundary). */
  author_id?: string | null;
  /** Generated short conversation title (≠ the raw first message). Produced by
   *  the server-side card-summarization pass; absent until it has run once. */
  topic?: string | null;
  /** One-line latest agent result rendered on the conversation card. Server-
   *  generated; absent until the summarization pass runs. */
  result_summary?: string | null;
  /** Top citation backing `result_summary` — the card's source chip. Null when
   *  the latest agent result carried no citation. */
  primary_source?: { label: string; ref: string } | null;
  /** Conversation card state: `active` (live) | `done` (archived/resolved) |
   *  `needs_review` (the agent flagged the latest result for nurse review).
   *  Server-derived; defaults to `active` until the first summarization. */
  conversation_state: 'active' | 'done' | 'needs_review';
  /** What the nurse opened this conversation to achieve, in her own words.
   *  Absent when she stated none, which is not the same as an empty one, so
   *  do not collapse the two. Never sent to any agent (adr/0013). */
  goal?: string;
};
export type SessionWithMessagesResponse = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  tags?: string[];
  /** Enriched tag information with labels (included when enrich_tags=true). */
  tag_info?: TagInfoResponse[] | null;
  /** When session was archived (null if not archived). */
  archived_at?: string | null;
  /** @default "pending" */
  is_shared?: boolean;
  agent_slug: string;
  /** See SessionResponse.agent — the open conversation view reads THIS shape,
   *  so it is the surface where a retired assistant has to be visible. */
  agent?: Reference;
  /** See SessionResponse.opening_turn_id. */
  opening_turn_id?: string;
  state?: unknown;
  /** A page of the transcript, as `GET /sessions/{id}/messages` reads it. */
  messages: Page<MessageResponse>;
  /** See SessionResponse.parent_session_id — drives the child "return to parent
   *  session" affordance (the parent can be any session type, not just chat). */
  parent_session_id?: string | null;
  /** See SessionResponse.patient_id. */
  patient_id?: string | null;
  /** See SessionResponse.author_name — the detail view (`GET /sessions/{id}`)
   *  also carries the creator's display name for the cross-nurse byline. */
  author_name?: string | null;
  /** See SessionResponse.author_id — drives viewer-vs-author detection on the
   *  detail view (e.g. the Shift Planner's read-only mode). */
  author_id?: string | null;
  /** See SessionResponse.topic. */
  topic?: string | null;
  /** See SessionResponse.result_summary. */
  result_summary?: string | null;
  /** See SessionResponse.primary_source. */
  primary_source?: { label: string; ref: string } | null;
  /** See SessionResponse.conversation_state. */
  conversation_state: 'active' | 'done' | 'needs_review';
  /** See SessionResponse.goal. */
  goal?: string;
};
/** Clinical case summary for handover documentation. */
export type SessionSummaryResponse = {
  session_id: string;
  /** Main clinical issue or question discussed in the session. */
  chief_concern: string;
  /** Important clinical findings, decisions, or information discovered. */
  key_findings: string[];
  /** Clinical recommendations or action items from the conversation. */
  recommendations: string[];
  /** Open questions the clinician should still resolve at handover. */
  open_questions: string[];
  generated_at: string;
};

// -- Message --
/**
 * Multi-agent attribution — which agent produced a turn, and how to render it.
 *
 * ONE definition on purpose. This is both the wire shape on
 * `MessageResponse.agent` and the shape the in-flight turn store carries, and
 * MessageBubble falls back from one to the other
 * (`storeAttribution ?? message.agent`) — two structurally-identical
 * declarations would let the two halves of that expression drift apart with
 * nothing to catch it.
 */
export interface AgentAttribution {
  agentId: string;
  agentName?: string;
  agentRole?: string;
  avatarUrl?: string;
}

// `content_type` is the manual-schema source of truth — see
// `MessageResponseSchema` in api/schemas.ts and the messages.content_type
// column in specs/db/care-app.sql. Drives the
// shouldRenderContentText gate in MessageBubble: structured-envelope types
// (protocol_step, system_prompt) suppress raw-JSON text leakage.
export type MessageResponse = {
  id: string;
  session_id: string;
  /** Who wrote it: a person's id, or empty when the PLATFORM composed it on an
   *  agent's behalf. See MessageResponseSchema for why rendering depends on it. */
  author_id?: string | null;
  role: string;
  content: string;
  citations?: CitationResponse[];
  /** Colleague expertise suggestions from expert search. */
  colleague_suggestions?: ColleagueExpertResponse[] | null;
  created_at: string;
  latency_ms?: number | null;
  content_summary?: string | null;
  content_details?: string | null;
  image_url?: string | null;
  /** User feedback type: positive, negative, or None. */
  /** The CALLER's own rating of this message; null when they have not rated
   *  it. Never the message's -- two nurses on one session hold two ratings. */
  feedback_type: string | null;
  /** Confidence scoring for assistant messages (None for user messages). */
  confidence?: ConfidenceMetadata | null;
  blocks?: ContentBlock[] | null;
  /** Multi-agent attribution for multi-agent workflows. */
  agent?: AgentAttribution | null;
  /** Free-string rendering hint. Blocks[].type is the content authority (Layer A retired). */
  content_type?: string;
  /** Event-message data. Absent on conversational messages. */
  payload?: unknown;
  /** Token usage and cost for assistant messages (None for user messages). */
  token_metrics?: {
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    cost_usd: number;
  } | null;
  /**
   * Typed triples graph attached by the search enrichment pipeline.
   * Absent when the knowledge search returned no related resources (no_data
   * envelope) or when the message was not produced by a knowledge search.
   *
   * Wire shape source-of-truth: `specs/semantic-layer/schemas/triples/schema.proto`
   * IRI vocabulary: `specs/semantic-layer/triples-vocab.md`
   *
   * The backend attaches this after reading `data.triples` from the
   * knowledge/v1 envelope. Frontend treats this field as opaque until
   * `parseTripleGraph` validates it.
   *
   * Previously a second `triples/v1` TextContent sibling; that piggyback
   * shape is gone.
   */
  triples?: import('@/lib/triples').TripleGraph | null;
  /**
   * True when the triples envelope carried a `TRIPLES_UNAVAILABLE`
   * warning code — meaning the upstream triple-enrichment lookup
   * failed (DB hiccup, statement timeout). Distinct from `triples == null`
   * with `triples_unavailable == false` which means "knowledge search
   * succeeded but no related resources exist for this query."
   *
   * Without this signal the bedside clinician cannot distinguish "DB
   * temporarily down" from "genuinely no protocols match" — both look
   * like zero chips. The TripleChips renderer reads this flag and
   * surfaces an explicit "Verwandte Ressourcen nicht verfügbar"
   * affordance so the user knows to retry later.
   *
   * Plumbed from `knowledge.SearchResponse.triples_unavailable` (proto)
   * through `upstream.Result.TriplesUnavailable` (mcp-server) into the
   * knowledge/v1 envelope's `warning_codes`, then projected onto this
   * field by the api-service MessageResponse builder.
   */
  triples_unavailable?: boolean;
  /**
   * True when the MCP client dropped at least one EmbeddedResource
   * because its Resource.Text exceeded the per-server
   * MaxResourceTextBytes cap (256 KiB). Distinct from a missing card
   * due to a tool error — in this case the tool call succeeded but
   * the payload was silently truncated at the MCP client layer.
   *
   * Plumbed via `ui_resource_dropped:true` in `Result.Flags`
   * (adapter.go) through `toolMetadataHasFlag` in response_formatter.go
   * onto this field — same pattern as `triples_unavailable`.
   *
   * When true, the UI should render a dismissible "A resource card was
   * omitted because the server returned an oversized payload" banner so
   * the bedside clinician is not left wondering why an expected card is
   * absent.
   */
  ui_resource_dropped?: boolean;
};
// Mirrors of the dead generator's MessageResponse and its FLATTENED widget
// block shapes (pre-envelope; see the "Agentic UI Content Blocks" comment
// below for the wire-correct `{type, data}` shapes). Referenced ONLY by
// SendMessageResponse, whose user_message/assistant_message were typed
// against the old generated schema — kept verbatim so this refactor is a
// type-level no-op.
type GenClarificationBlock = {
  type: "clarification";
  question: string;
  options: ClarificationOption[];
  style: "chips" | "buttons" | "dropdown";
  allow_multiple: boolean;
};
type GenConfirmationBlock = {
  type: "confirmation";
  title?: string;
  message: string;
  confirm_label?: string;
  cancel_label?: string;
  risk_level?: string;
};
type GenInputFormBlock = {
  type: "input_form";
  form_title: string;
  fields: InputField[];
  explanation: string;
  submit_label: string;
};
type GenMessageResponse = {
  id: string;
  session_id: string;
  role: string;
  content: string;
  citations?: CitationResponse[];
  colleague_suggestions?: ColleagueExpertResponse[] | null;
  created_at: string;
  latency_ms?: number | null;
  content_summary?: string | null;
  content_details?: string | null;
  image_url?: string | null;
  blocks?: (TextBlock | GenClarificationBlock | GenConfirmationBlock | GenInputFormBlock)[] | null;
  /** The CALLER's own rating of this message; null when they have not rated
   *  it. Never the message's -- two nurses on one session hold two ratings. */
  feedback_type: string | null;
  confidence?: ConfidenceMetadata | null;
  token_metrics?: {
    input_tokens: number;
    output_tokens: number;
    cache_read_tokens: number;
    cost_usd: number;
  } | null;
};

/**
 * Response from sending a message — includes both user and assistant
 * messages. This eliminates the need for optimistic updates with fake IDs
 * in the frontend, preventing race conditions between cache updates and
 * invalidation refetches.
 */
export type SendMessageResponse = {
  /** The user's message with real ID from database. */
  user_message: GenMessageResponse;
  /** The assistant's response with citations and blocks. */
  assistant_message: GenMessageResponse;
  /** List of degraded services (e.g., ['snomed']) with open circuit breakers. */
  degraded_services?: string[] | null;
};
export type FeedbackRequest = {
  type: string;
  text?: string | null;
};
/**
 * Why a citation is attached to a message. Mirrors the llm.v1.Provenance proto
 * enum via chat.CitationProvenance (tricorder-go/chat/types.go).
 *
 * `cited` means a claim in the answer is grounded on the chunk; `retrieved`
 * means a tool surfaced it during the agent loop and nothing is grounded on it.
 */
export type CitationProvenance = 'cited' | 'retrieved';

export type CitationResponse = {
  document: string;
  page_start: number;
  page_end: number;
  cited_text: string;
  /** @default "document" */
  source_type: string;
  url?: string | null;
  /** Source authority tier: 1=SAMW/Swissnoso, 2=SGI/BAG, 3=web, 4=LLM knowledge. */
  source_tier?: number | null;
  /** Cosine similarity score from vector retrieval (0.0-1.0). */
  retrieval_score?: number | null;
  /**
   * Whether the answer is grounded on this citation, as the backend marked it.
   * Two shapes mean "nobody marked it": `''`, which the Go struct emits for an
   * unmarked citation, and absent, which is every row written into
   * messages.citations before the field existed — that column is returned to
   * the client verbatim, so those rows never gain the key.
   */
  provenance?: CitationProvenance | '';
  /**
   * Which retrieved passage this citation points at, so a diagnostics surface
   * can reach the passage behind a page number. A corpus hit carries the
   * chunk's id and a medication result carries the Fachinformation section's
   * id. Empty when the producer matches a record or a whole document instead:
   * terminology, dm+d and the compatibility servers. Absent on rows written
   * before the field existed.
   */
  chunk_id?: string;
  /**
   * Which DOCUMENT the cited passage belongs to, as knowledge-service stamped
   * it. Empty when the citation names a record rather than a corpus document:
   * terminology, dm+d, the compatibility servers and a medication result's
   * Fachinformation sections. Absent on rows written before the field existed.
   */
  document_id?: string;
};
export type ColleagueExpertResponse = {
  name: string;
  specialty: string;
  years_experience: number;
};
/**
 * Confidence scoring metadata for assistant messages (CUJ-22). Captures
 * retrieval quality signals aggregated across all tool executions for a
 * single assistant response.
 */
export type ConfidenceMetadata = {
  /** Confidence tier: high/medium/low. */
  tier: "high" | "medium" | "low";
  /** Highest cosine similarity score across all retrievals. */
  retrieval_score_top?: number | null;
  /** Average cosine similarity score across all retrievals. */
  retrieval_score_avg?: number | null;
  /**
   * Number of sources used in response.
   * @default 0
   */
  source_count: number;
  /** Best (lowest number) source tier used: 1=SAMW/Swissnoso, 2=SGI, 3=web, 4=LLM. */
  source_tier?: number | null;
  /** Human-readable explanation of confidence assessment. */
  reasoning: string;
  /** Signals that affected scoring: clarification_needed, embedding_warming, etc. */
  flags?: string[];
  /**
   * True when at least one tool returned MCP envelope confidence:stale
   * (source past valid_until). Orthogonal to tier — frontend renders an
   * explicit "expired source" sub-badge regardless of tier.
   */
  has_stale_source?: boolean;
};
/** Enriched tag information with human-readable label. */
export type TagInfoResponse = {
  code: string;
  label: string;
  vocabulary: string;
};

// -- Agentic UI Content Blocks --
//
// Wire shape for all blocks is `{type, data: {…}}` per api-service-go's
// universal ContentBlock envelope (`api-service-go/types.go::ContentBlock`
// — `Data json.RawMessage`, decoded per `Type`). The (now-deleted) generated
// OpenAPI types for the three widget blocks were inconsistent: ProtocolStep
// and MCPUIResource correctly nested fields under `data`, while Clarification,
// Confirmation, and InputForm flattened them at the top level. That drift
// crashed `e.options.map` the first time the agent actually emitted a
// `<ui_select>` widget (2026-05-22 bedside repro). These are the
// wire-correct shapes; the renderers read through `block.data.X`.
export type TextBlock = {
  type: "text";
  /** Text content to display. */
  content: string;
};
/** Single option in a clarification prompt. */
export type ClarificationOption = {
  /** Unique identifier for the option. */
  id: string;
  /** Display label (e.g., medication name). */
  label: string;
  /** Additional context (e.g., indication). */
  description?: string | null;
};
export type ClarificationBlock = {
  type: 'clarification';
  data: {
    id?: string;
    question: string;
    options: ClarificationOption[];
    style: 'chips' | 'buttons' | 'dropdown';
    allow_multiple: boolean;
  };
};
export type ConfirmationBlock = {
  type: 'confirmation';
  data: {
    id?: string;
    /** Absent unless the model authored one — the renderer localises. */
    title?: string;
    message: string;
    /** Absent unless the model authored one — the renderer localises. */
    confirm_label?: string;
    /** Absent unless the model authored one — the renderer localises. */
    cancel_label?: string;
    /**
     * What the model declared, verbatim. "high" styles the dialog prominently;
     * anything else, including absent, renders plainly. Not narrowed to a union
     * because the parser does not police the vocabulary.
     */
    risk_level?: string;
  };
};
/** Single field in an input form. */
export type InputField = {
  /** Field identifier. */
  name: string;
  /** User-facing label. */
  label: string;
  /** Input type. */
  field_type: "text" | "number" | "date" | "select";
  /**
   * Whether field is required.
   * @default true
   */
  required: boolean;
  placeholder?: string | null;
  /** Unit of measurement (e.g., 'kg'). */
  unit?: string | null;
  /** Minimum value (number). */
  min_value?: number | null;
  /** Maximum value (number). */
  max_value?: number | null;
  /** Options for select fields. */
  options?: string[] | null;
};
export type InputFormBlock = {
  type: 'input_form';
  data: {
    id?: string;
    form_title: string;
    fields: InputField[];
    explanation: string;
    submit_label: string;
  };
};
// Allocation proposal block — the allocation agent's proposed patient→nurse
// pairs. `data` stays `unknown` at the wire layer; AllocationProposalCard
// narrows it with isAllocationCommands before rendering.
export interface AllocationCommandsBlock {
  type: 'allocation_commands';
  data: unknown;
}

// Access-command block — the team-access agent's proposed grant/revoke/approve/
// deny changes. `data` stays `unknown` at the wire layer; foldAccess narrows it
// with isAccessCommands before rendering AccessProposalCards (mirrors
// AllocationCommandsBlock). Must also be a member of the ContentBlock union +
// the Zod ContentBlockSchema, or a team-access session with proposals fails the
// whole GET /sessions parse.
export interface AccessCommandsBlock {
  type: 'access_commands';
  data: unknown;
}

// Profile-field extraction block — emitted by the agentic onboarding
// interview's profile_extractor post-processor, one per assistant turn,
// CUMULATIVE (a later block carries the full picture so far, not a delta).
// The frontend never reads it: the onboarding agent writes the profile itself
// through PUT /me, so `data` stays `unknown` (mirrors AllocationCommandsBlock
// and AccessCommandsBlock). Must also be a member of the ContentBlock union +
// the Zod ContentBlockSchema, or an onboarding session fails the whole
// GET /sessions parse.
export interface ProfileFieldsBlock {
  type: 'profile_fields';
  data: unknown;
}

// Protocol step block — emitted by api-service-go's protocol dispatcher
// (`protocol_dispatcher.go::persistProtocolStep`) when a protocol intent
// is dispatched. Carries the full renderable content for the current node
// so the frontend never needs to parse the opaque protojson state.
//
// **Canonical wire shape source-of-truth:**
//   api-service-go/protocol_dispatcher.go::ProtocolStepPayload
//   specs/wire-shapes/protocol_step_block.json — asserted by both:
//     - Go: api-service-go/protocol_step_fixtures_test.go::TestMarshalFixture_ProtocolStepBlock
//     - TS: frontend/src/components/chat/agentic/ProtocolStepBlock.test.tsx
//           "Wire shape fixture — Zod schema contract"
//
// When a Go field changes (Go side first), also update:
//   - this interface
//   - `frontend/src/api/schemas.ts` — paired Zod validator
//   - `specs/wire-shapes/protocol_step_block.json` — shared fixture

/**
 * Observation input value type — the set of values the renderer currently
 * knows how to widget. Each value coerces the captured input to its JSON
 * type before it is placed in the dispatched `payload`:
 *   - "string"  → string (text input)
 *   - "integer" → number, no decimals (number input, step=1)
 *   - "float"   → number, decimals allowed (number input, step=any)
 *   - "boolean" → boolean (checkbox)
 *
 * Default when unset is "string". The proto carries value_type as an
 * `ObservationValueType` enum on the Action message
 * (tricorder-protos/proto/tricorder/protocol/v1/protocol.proto); the dispatcher
 * (api-service-go/protocol_dispatcher.go::observationValueTypeWireString)
 * converts the enum to these lower-case wire strings before serialising the
 * NodeContent block.
 *
 * The wire field is typed `string` (NOT this literal union) on
 * `ProtocolNodeAction.value_type` for forward-compat: a future backend
 * value_type the frontend doesn't know about must still parse so the
 * renderer's `resolveValueType` fallback can map it to "string" (safe
 * default). This `ProtocolObservationValueType` literal alias is the
 * known-set used by the renderer's widget dispatch.
 */
export type ProtocolObservationValueType =
  | 'string'
  | 'integer'
  | 'float'
  | 'boolean';

/** A single action the user can take from the current node. */
export interface ProtocolNodeAction {
  /** Action identifier used in edge conditions (e.g. "continue", "ja"). */
  id: string;
  /** Localised button label. */
  label: string;
  /**
   * Serialised ActionKind enum string.
   * "ACTION_KIND_CONFIRM" for single-step nodes.
   * "ACTION_KIND_CHOICE" for decision branches.
   * "ACTION_KIND_OBSERVATION_INPUT" for OBSERVATION nodes — paired with a
   * `value_type` widget; the captured value is dispatched in `payload[payload_key]`.
   */
  kind?: string;
  /**
   * For ACTION_KIND_OBSERVATION_INPUT only. The JSON value type the user is
   * asked to enter; selects the input widget and validates the value before
   * dispatch. Unset/unknown → treated as "string". Ignored for non-INPUT kinds.
   *
   * Typed as `string` (not the literal `ProtocolObservationValueType` union)
   * so that a future backend value_type the frontend doesn't yet know about
   * still parses cleanly via the Zod schema and reaches the renderer's
   * safe-default fallback. `ProtocolObservationValueType` is the known-set
   * the widget dispatch matches against.
   */
  value_type?: string;
  /**
   * For ACTION_KIND_OBSERVATION_INPUT only. The key under which the captured
   * value is placed in the dispatched `payload` object (e.g. "hr", "spo2").
   * Defaults to the action's `id` when unset — keeps simple protocols terse.
   * Ignored for non-INPUT kinds.
   */
  payload_key?: string;
  /**
   * For ACTION_KIND_OBSERVATION_INPUT only. Optional unit suffix shown next to
   * the input ("mg/dL", "bpm"). Pure presentation — never dispatched.
   */
  unit?: string;
  /**
   * For ACTION_KIND_OBSERVATION_INPUT only. Optional placeholder shown in the
   * input until the user types. Pure presentation — never dispatched.
   */
  placeholder?: string;
}

/**
 * Serialised Tone enum string. Mirrors `protocolpb.Tone` enum (proto
 * source-of-truth: tricorder-protos/proto/tricorder/protocol/v1/protocol.proto).
 *
 * Backend omits the field when the protocol leaves Tone unset
 * (TONE_UNSPECIFIED); the renderer treats absence and TONE_UNSPECIFIED
 * identically — neutral, no left-edge strip.
 */
export type ProtocolTone =
  | 'TONE_UNSPECIFIED'
  | 'TONE_OK'
  | 'TONE_WARN'
  | 'TONE_BAD'
  | 'TONE_MUTED';

/** Localised renderable content for a protocol node. */
export interface ProtocolNodeContent {
  /** Localised node title. */
  title: string;
  /**
   * Body bullet points, EMPTY when the node has none. Required, not optional:
   * the dispatcher always serialises the key.
   */
  body: string[];
  /**
   * Available actions, EMPTY on a terminal node. Required for the same reason,
   * and it is the one that matters: optional meant a payload missing `actions`
   * parsed fine and rendered as a step with no way forward, so a nurse
   * mid-protocol saw a card that did nothing and no reason why.
   */
  actions: ProtocolNodeAction[];
  /**
   * Tone enum string. Drives the left-edge tone strip and the verdict
   * line colour. Empty/absent → neutral (no strip).
   */
  tone?: ProtocolTone;
  /**
   * Prominent verdict / question line rendered above the body bullets.
   * Pre-interpolated by the dispatcher. Empty/absent → no verdict line.
   */
  verdict?: string;
  /**
   * Reference detail for the step: what the clinician LOOKS UP, as opposed to
   * `body`, which is what they DO. Dosing, energy values, the guideline
   * section an answer came from. Rendered muted under the body and collapsed
   * until tapped. Pre-interpolated. Empty/absent → nothing renders.
   */
  reference?: string;
}

/** Data payload inside a protocol_step ContentBlock. */
export interface ProtocolStepBlockData {
  /** Protocol catalog identifier (e.g. "test_runbook"). */
  protocol_id: string;
  /** Protocol catalog version (e.g. "v1"). */
  protocol_version: string;
  /** Current node identifier (e.g. "step1"). */
  node_id: string;
  /**
   * Serialised NodeKind enum string.
   * "NODE_KIND_STEP" | "NODE_KIND_DECISION" | "NODE_KIND_OBSERVATION" | "NODE_KIND_ACTION" | "NODE_KIND_TERMINAL"
   */
  node_kind?: string;
  /** Renderable content for the current node. */
  node_content?: ProtocolNodeContent;
  /**
   * Opaque protojson-encoded ProtocolState. The frontend renderer does NOT
   * parse this — it is carried for debugging and for server-side state folding.
   *
   * Documented exception: `MessageList.tsx::isBootstrapStep` reads
   * `state.history` (length only — boolean "is the run brand new") to drive
   * bootstrap-run-id React keying. That's the ONE place outside the backend
   * that touches this field. If `ProtocolState.history` is renamed on the
   * Go side, update `isBootstrapStep` in the same change.
   */
  state: unknown;
}

/** protocol_step content block — rendered by ProtocolStepBlock.tsx. */
export interface ProtocolStepBlock {
  type: 'protocol_step';
  data: ProtocolStepBlockData;
}

/**
 * Data payload inside a training_step ContentBlock — the training-domain
 * twin of ProtocolStepBlockData. Identical shape apart from
 * `component_id`/`component_version` in place of `protocol_id`/`protocol_version`.
 * Never rendered as an interactive inline card — see UIComponentRenderer.tsx's
 * `training_step` case — but registered so a session carrying one still
 * validates (same H6/forward-compat reasoning as protocol_step).
 */
export interface TrainingStepBlockData {
  /**
   * Training component catalog identifier (e.g. "hypoglycemia-quiz").
   * Named protocol_id on the wire, NOT component_id: the training runtime
   * persists its step audit through the shared protocol-step writer, because
   * a training component IS a protocol graph. See TrainingStepBlockDataSchema.
   */
  protocol_id: string;
  /** Training component catalog version (e.g. "v1"), as protocol_version. */
  protocol_version: string;
  node_id: string;
  node_kind?: string;
  node_content?: ProtocolNodeContent;
  /** Opaque protojson-encoded ProtocolState — same runtime as protocol_step. */
  state: unknown;
}

/** training_step content block — registered but not inline-rendered (see UIComponentRenderer.tsx). */
export interface TrainingStepBlock {
  type: 'training_step';
  data: TrainingStepBlockData;
}

// MCP-UI resource block — emitted by the chat post-processor
// (`mcp_ui_extractor`) for any MCP tool result that includes a
// UIResource alongside its envelope (ADR-003 §3). Generic across
// classes: any tool/server can produce one, the renderer dispatches
// purely on `mimeType`. Y-site is the first consumer.
//
// **Canonical wire shape source-of-truth:**
// `tricorder-go/mcpui` (resource.go) + `tricorder-go/chat`
// (mcp_ui_extractor.go's MCPUIResourceBlockData). This TS interface
// must mirror the Go struct. When a field changes (Go side first),
// also update:
//   - `frontend/src/api/schemas.ts` — the Zod runtime validator
//   - `frontend/src/components/chat/agentic/MCPUIResourceBlock.test.tsx`
//     — the wire-shape contract test
// Drift caught by `specs/wire-shapes/mcp_ui_resource_block.json` —
// both sides assert against it in tests.
//
// Wire shape per @mcp-ui/client v5.x dispatch:
//   uri      — `ui://<class>/<tool>/<contentHash>` (stable per render)
//   mimeType — `text/html` (rawHtml), `text/uri-list` (externalUrl),
//              or `application/vnd.mcp-ui.remote-dom*` (remoteDom).
//              v5 client uses STRICT EQUALITY without trailing
//              parameters; do NOT add `;profile=mcp-app`.
//   text     — the payload (HTML for rawHtml, URL for externalUrl,
//              JS bundle for remoteDom — kept as `text` to match
//              the MCP wire spec)
//   tool     — optional, host-side observability metadata; NOT part
//              of the MCP-UI resource shape and MUST NOT be passed
//              into `UIResourceRenderer`'s `resource` prop.
export interface MCPUIResourceBlockData {
  uri: string;
  mimeType: string;
  text: string;
  tool?: string;
}
export interface MCPUIResourceBlock {
  type: 'mcp_ui_resource';
  data: MCPUIResourceBlockData;
}

// ============================================================================
// Elicitation SSE event — `elicitation_prompt` event emitted by
// api-service-go when an MCP tool calls mcpserver.Elicit.
// ============================================================================
//
// When the Go-side wire shape changes (Go side first), also update:
//   - this interface,
//   - `frontend/src/api/schemas.ts` (the paired Zod validator).
export interface ElicitationPromptEvent {
  /** SSE envelope event type — always "elicitation_prompt". */
  event: 'elicitation_prompt';
  /** Server-generated UUID correlating the prompt with its ResolveElicitation call. */
  elicitationId: string;
  /** Human-readable message explaining what is being requested. */
  message: string;
  /**
   * JSON Schema (flat per MCP 2025-06-18 spec) describing the expected
   * response shape. Carried as a raw JSON string.
   */
  requestedSchemaJson: string;
}

// ============================================================================
// UIAction wire types — POST /api/v1/sessions/{id}/ui_actions
// ============================================================================
// Card-emitted actions (postMessage from MCP-UI iframe) that the host
// forwards to the backend. Mirrors @mcp-ui/client's UIActionResult.
//
// **Canonical wire shape source-of-truth:**
//   - Proto: `tricorder-protos/proto/tricorder/mcpui/v1/action.proto`
//   - Generated Go: `tricorder-go/pb/tricorder/mcpui/v1/action.pb.go`
//   - JSON fixtures: `specs/wire-shapes/ui_action_request.json`,
//                    `specs/wire-shapes/ui_action_acknowledgment.json`
//
// Wire format is default protojson: camelCase field names, omits
// unpopulated fields, enum encoded as the symbolic name string.
//
// When a proto field changes (proto first), also update:
//   - `frontend/src/api/schemas.ts` — paired Zod validators
//   - `frontend/src/api/uiactions.test.ts` (T2.6) — fixture-vs-interface
//     contract tests; that file is what catches drift.

/** Card asks the agent to re-call an MCP tool with the supplied params. */
export interface ToolAction {
  toolName: string;
  /**
   * JSON object form of `google.protobuf.Struct`. Optional: as a
   * proto3 message field, an unset Struct is omitted from the wire
   * by default protojson (so `{ toolName: "x" }` with no params is
   * a valid payload).
   */
  params?: Record<string, unknown>;
}

/** Semantic signal from the card (e.g. "user clicked 'ask pharmacy'"). */
export interface IntentAction {
  intent: string;
  /** JSON object form of `google.protobuf.Struct`. Optional — see `ToolAction.params`. */
  params?: Record<string, unknown>;
}

/** Fill the chat input with text (user decides whether to send). */
export interface PromptAction {
  prompt: string;
}

/** Open a URL. Cross-origin URLs require a confirm dialog (Spec 03). */
export interface LinkAction {
  url: string;
}

/** Show a toast. */
export interface NotifyAction {
  message: string;
}

// Wire-faithful shape mirroring the proto `oneof` JSON encoding
// (exactly one key populated). A `JSON.parse` of the fixture lands
// in this type with no transformation. Narrow via `'tool' in action`.
export type UIAction =
  | { tool: ToolAction }
  | { intent: IntentAction }
  | { prompt: PromptAction }
  | { link: LinkAction }
  | { notify: NotifyAction };

export interface UIActionRequest {
  /**
   * Card-originated actions carry the source block URI; a fabric SurfaceEvent
   * (GuideEngine) does not — optional so both ride the same POST body.
   */
  sourceBlockUri?: string;
  /** Optional — proto `omitempty`; absent on the wire when empty. */
  sourceTool?: string;
  /** Absent when this request carries a `surfaceEvent` instead of a card action. */
  action?: UIAction;
  /**
   * Agentic UI Fabric host->agent feedback. When set, `action`/`sourceBlockUri`
   * are absent and the backend publishes it as a `surface_event` (no dispatch).
   */
  surfaceEvent?: SurfaceEventWire;
}

/**
 * Acknowledgment status — protojson encodes the proto enum as its
 * symbolic name string. All seven values from `UIActionAcknowledgment.Status`.
 */
export type UIActionAcknowledgmentStatus =
  | 'STATUS_UNSPECIFIED'
  | 'STATUS_RECEIVED'
  | 'STATUS_OK'
  | 'STATUS_UNAUTHORIZED'
  | 'STATUS_TOOL_FAILED'
  | 'STATUS_VALIDATION_FAILED'
  | 'STATUS_RATE_LIMITED';

export interface UIActionAcknowledgment {
  /** Server echoes the client-generated Idempotency-Key. */
  actionId: string;
  status: UIActionAcknowledgmentStatus;
  /** Optional — present when status indicates failure. */
  errorMessage?: string;
}

// ============================================================================
// UIAction SSE event payloads — body of `ui_action_started` /
// `ui_action_result` / `ui_action_error` server-sent events
// ============================================================================
// These are NOT proto types: the dispatcher publishes them via stdlib
// `encoding/json`, not protojson. Field names below mirror the Go struct
// JSON tags one-for-one (camelCase, omitempty on optional fields).
//
// **Canonical wire shape source-of-truth:**
//   - Go struct: `api-service-go/uiactions/events.go`
//     (`UIActionStartedEvent`, `UIActionResultEvent`, `UIActionErrorEvent`)
//   - JSON fixtures: `specs/wire-shapes/ui_action_started_event.json`,
//                    `ui_action_result_event.json`, `ui_action_error_event.json`
//   - Drift detection: Go side asserts via
//     `api-service-go/uiactions/events_test.go::TestMarshalFixture_*`;
//     frontend side asserts via `frontend/src/api/uiActionEvents.test.ts`.
//
// When a Go struct field changes (Go side first), also update:
//   - this file (the matching TS interface),
//   - `frontend/src/api/schemas.ts` (the paired Zod validators),
//   - the fixture JSON, and
//   - the matching `events_test.go` Go test case.
// `AssertEqual` in `schemas.ts` and the fixture-vs-interface tests will
// fail otherwise.

/**
 * `ui_action_started` payload. Audit-only marker emitted the moment a
 * `UIActionRequest` is accepted but before the MCP tool dispatch returns —
 * so the LLM sees "the user clicked X" in next-turn history. The frontend
 * doesn't need to act on this (the click-time `register` in T3.6 already
 * marks the action pending); we keep classifying it so a future use case
 * (e.g. cross-tab fanout) has a typed entry point.
 */
export interface UIActionStartedEvent {
  actionId: string;
  /** Empty for non-tool actions; protojson-style `omitempty`. */
  toolName?: string;
  sourceBlockUri: string;
  /** RFC3339 UTC. */
  startedAt: string;
}

/**
 * `ui_action_result` payload. Carries the new ContentBlock produced by a
 * successful tool dispatch. `block` is optional because frontend-only
 * actions (prompt/link/notify) and side-effect-only tools complete with
 * no new UIResource — see the `omitempty` rationale in the Go struct doc.
 */
export interface UIActionResultEvent {
  actionId: string;
  sessionId: string;
  block?: MCPUIResourceBlockData;
}

/**
 * `ui_action_error` payload. `status` is the symbolic name of
 * `mcpuiv1.UIActionAcknowledgment_Status` (e.g. `"STATUS_TOOL_FAILED"`),
 * matching the synchronous acknowledgment's `status` so frontend code can
 * switch on the same union.
 */
export interface UIActionErrorEvent {
  actionId: string;
  status: UIActionAcknowledgmentStatus;
  /** Optional — usually present, but not required by the wire contract. */
  errorMessage?: string;
}

// Inbound-summary block — LEGACY. Carried by historical `inbound_summary`
// messages that the now-removed child-push hand-off path wrote (PR #1558). The
// child no longer pushes a summary to its parent (that endpoint is gone); the
// parent PULLS one live via GET /sessions/{id}/summary instead. These messages
// still exist in production and MUST keep rendering, so the block type stays:
// `content_type='inbound_summary'`, `role='agent'`, summary prose in `content`,
// exactly this block at `blocks[0]` carrying the child's routing identity.
export interface InboundSummaryBlock {
  type: 'inbound_summary';
  child_session_id: string;
  child_agent_slug: string;
}

// ResourceSummaryResponse (GET /sessions/{id}/summary) is defined as a
// Zod-inferred type in api/schemas.ts (ResourceSummaryResponseSchema) to avoid a
// parallel interface + schema drifting — getSessionSummary returns the inferred
// shape.

// Learn-handoff block — `{type:'training_handoff', data:{component_id, component_version}}`.
// Emitted by the chat pipeline's TrainingHandoffBlockStep; rendered by
// TrainingHandoffCard as an "open in learn app" button. Wire-shape source of
// truth: tricorder-go/protocol/training_handoff_flag.go (the flag contract) —
// the content-block wrapper mirrors tricorder-go/chat/types.go::TrainingHandoffBlock.
export interface TrainingHandoffBlock {
  type: 'training_handoff';
  data: {
    component_id: string;
    component_version: string;
  };
}

// Sentinel discriminator for a content block whose real `type` this client
// version doesn't recognise (forward-compat). A DISTINCT literal (not a bare
// `string`) keeps the ContentBlock discriminated union disjoint, so every known
// block still narrows in a `switch (block.type)`. The real wire type is preserved
// in `unknownType` for logging.
export const UNKNOWN_BLOCK_TYPE = '__unknown__' as const;

// Forward-compatibility fallback for a content block whose `type` this client
// version doesn't recognise (a newer backend added it). Parsed (and remapped to
// the sentinel) by the UnknownContentBlockSchema arm so an unknown type can never
// throw the whole GET /sessions response; rendered as an "unsupported content"
// placeholder.
export interface UnknownContentBlock {
  type: typeof UNKNOWN_BLOCK_TYPE;
  unknownType: string;
  data?: unknown;
}

export type ContentBlock =
  | TextBlock
  | ClarificationBlock
  | ConfirmationBlock
  | InputFormBlock
  | AllocationCommandsBlock
  | AccessCommandsBlock
  | MCPUIResourceBlock
  | ProtocolStepBlock
  | InboundSummaryBlock
  | ProfileFieldsBlock
  | TrainingStepBlock
  | TrainingHandoffBlock
  | UnknownContentBlock;

// -- Bookmark --
export type CreateBookmarkRequest = {
  /** Message ID to bookmark. */
  message_id: string;
  /** The session the message belongs to — required from the session cut-over
   *  on: the gateway verifies the pair over session/v1 (the message is
   *  tricorder-session's; there is no local table to resolve it against). */
  session_id: string;
};
/**
 * Wire shape of `BookmarkResponse.enrichment_metadata` — the booleans are
 * always present on the wire. The exported `EnrichmentMetadata` below is the
 * frontend-friendly variant with optional booleans.
 */
type GenEnrichmentMetadata = {
  /** @default 0 */
  attempt_count: number;
  /** @default 3 */
  max_attempts: number;
  started_at?: string | null;
  completed_at?: string | null;
  duration_ms?: number | null;
  failure_reason?: FailureReason | null;
  last_error?: string | null;
  failed_phase?: ("orchestrator" | "research" | "synthesis") | null;
  /** Earliest timestamp for next retry attempt. */
  retry_after?: string | null;
  /** @default false */
  pubmed_searched: boolean;
  /** @default false */
  sai_searched: boolean;
  /** Search queries attempted. */
  queries_used?: string[];
};
export type BookmarkResponse = {
  id: string;
  user_id: string;
  message_id: string;
  session_id: string;
  content_summary: string | null;
  content_details: string | null;
  citations?: CitationResponse[];
  /** Status of background bookmark enrichment. */
  enrichment_status: "pending" | "processing" | "completed" | "failed";
  enriched_at?: string | null;
  enrichment_metadata?: GenEnrichmentMetadata | null;
  pubmed_references?: PubMedReference[] | null;
  sai_references?: GenSAIReference[] | null;
  study_notes?: string | null;
  created_at: string;
  updated_at: string;
};
/** PubMed article reference. */
export type PubMedReference = {
  pmid?: string | null;
  title: string;
  authors?: string | null;
  abstract?: string | null;
  year?: string | null;
  url: string;
  /** Why this article is relevant. */
  relevance?: string | null;
};
/** SAI medication reference as the backend serialises it. */
type GenSAIReference = {
  name: string;
  indication?: string | null;
  dosage?: string | null;
  notes?: string | null;
};
export type SAIReference = GenSAIReference & {
  /** Frontend-only: fields used by BookmarkDetail that differ from backend schema. */
  section?: string | null;
  content_preview?: string | null;
  score?: number | null;
};

// -- Config --
/** Localized string values for each supported language. */
export type LocalizedStrings = {
  de: string;
  fr: string;
  en: string;
};
/** Branding configuration — allows deployment-specific app names. */
export type BrandingConfig = {
  /** App name in each language. */
  name: LocalizedStrings;
  /** App tagline in each language. */
  tagline: LocalizedStrings;
};
// -- Health --
export type HealthResponse = {
  status: "healthy" | "degraded" | "starting" | "unhealthy";
  /** API version. */
  version: string;
  /** Service check results: 'ok', 'error: <msg>', or 'loading'. */
  checks?: {
    [key: string]: string;
  };
};
/** Detailed health for a single service dependency. */
export type ServiceHealth = {
  status: "ok" | "degraded" | "unavailable";
  /** Health check latency in ms. */
  latency_ms?: number | null;
  /** Human-readable status detail. */
  message?: string | null;
};
/** Deep readiness check response for startup/readiness probes. */
export type ReadinessResponse = {
  /** Whether the app is ready to serve traffic. */
  ready: boolean;
  status: "healthy" | "degraded" | "starting" | "unhealthy";
  /** API version. */
  version: string;
  /** Per-service deep health check results. */
  services?: {
    [key: string]: ServiceHealth;
  };
  /**
   * Whether all startup initialization is finished.
   * @default false
   */
  startup_complete: boolean;
};

// ============================================================================
// Frontend-only Types (not in OpenAPI spec)
// ============================================================================

/** Message send request (sent as form data, not JSON body). */
export interface SendMessageRequest {
  content: string;
}

/**
 * One option of a profile field with a fixed vocabulary.
 *
 * - label_key: i18n key for translation (preferred, e.g. "new" -> profile.years.new)
 * - label: display label authored in config, used when no i18n key exists
 */
export type ProfileFieldOption = {
  label_key?: string | null;
  label?: string | null;
  value: number | string;
};

/** One profile field's option vocabulary, from GET /config. */
export type ProfileFieldOptions = {
  field: string;
  type: "text" | "chips" | "chips_with_freetext" | "range_buttons";
  options?: (string | ProfileFieldOption)[] | null;
  /** Pre-filled value for a deployment-fixed field (e.g. the institution). */
  default_value?: string | number | null;
  /** False means the deployment fixes this field and the person may not change it. */
  editable: boolean;
};

/** The profile's option vocabularies block of GET /config. */
type ProfileFieldsConfig = {
  fields: ProfileFieldOptions[];
};

/** Server-enforced input limits the frontend mirrors (char counters). Served
 *  on /config from api-service-go env config, so UI guards never drift from
 *  what the server validates. */
export interface LimitsConfig {
  max_feedback_comment_length: number;
}

/** Frontend cache stale times (loaded from config). */
export interface FrontendCacheConfig {
  sessions_stale_ms: number;
  session_detail_stale_ms: number;
  users_stale_ms: number;
}

export interface KnowledgeBaseCategory {
  key: string;
  label_en: string;
  label_de: string;
}

/** A terminology coding hung off a field VALUE, not off the field. */
export interface AttributeCoding {
  system: string;
  code: string;
  display?: string;
}

/**
 * One form-field descriptor, derived from the API's tailored `$schema`
 * document (see api/patientSchema.ts).
 *
 * It is a UI shape, not a wire shape: the wire carries JSON Schema, and this is
 * what a control needs out of one property of it. `ward` is NEVER a field here
 * — the ward is the patient's placement (`location_id` + encounter events), not
 * an attribute.
 *
 * Emptiness is loaded on every key, which is why none of them is optional: an
 * empty `classes` means "every class", an empty `pattern` means unconstrained
 * text, an empty `dedup` means no dedup check. A dropped key would turn a
 * declaration the unit made into a field the form cannot see.
 */
export interface PatientAttributeField {
  key: string;
  /** The JSON Schema type name ("string", "integer", …). Drives the control
   *  and the coercion on submit — a ward may declare an integer, and a form
   *  that posted "12" would be refused by the constraint that made it one. */
  type: string;
  required: boolean;
  /** A closed disjunction's members, rendered as a <select>; null for free
   *  text. */
  values: string[] | null;
  /** RE2 regex a value must fully match; empty when unconstrained. */
  pattern: string;
  /** "minted" means the SERVER produces the value. A minted field is absent
   *  from `$defs.create` altogether, so it never reaches a write form — this
   *  key is here for the read surfaces that explain where a value came from. */
  strategy: string;
  link_fields: string[] | null;
  dedup: string;
  /** i18n key the unit declared; empty when it declared none and the form
   *  falls back to `patients.attributes.<key>`. */
  label: string;
  classes: string[] | null;
  value_codings: Record<string, AttributeCoding> | null;
}

/**
 * Patient registration config (from GET /config).
 *
 * PHI rule: patients have NO name. There is no field list here any more: which
 * attributes exist is a property of (resource × profession × unit), which no
 * deployment-wide config block can state — the form reads
 * `GET /patients/$schema?location=<unit>` instead. `label_format`
 * (e.g. "{ward}-{bed}") is the template used to render a patient's display
 * label; its `{ward}` placeholder resolves from the patient's PLACEMENT
 * (location name), never from `attributes`.
 */
export interface PatientsConfig {
  /** Location kinds the registration unit picker offers (config-driven). */
  ward_kinds?: string[];
  label_format: string;
  /**
   * admission_ref pseudonymisation vocabulary (design §2/§4). Wire path is
   * `patients.admission_ref.disclosure_reason_codes` (api-service-go
   * PatientsAdmissionRefConfig.DisclosureReasonCodes) — the config-driven
   * disclosure reason-code list for the reveal endpoint's request body;
   * never hardcode reason codes in the frontend. Optional at both levels: an
   * older backend (or one that hasn't shipped this block yet) omits it, and
   * a shipped-but-empty vocabulary is a real, distinct state — consumers
   * (AdmissionRefSection.tsx) must render an explicit "unavailable" error
   * and disable the reveal flow for EITHER case, never silently default to
   * an empty-but-seemingly-functional picker.
   */
  admission_ref?: {
    disclosure_reason_codes?: string[];
  };
}

/**
 * Config-driven stay vocabulary, from the GET /config `stay` block
 * (api-service-go `StayConfig`). Drives every class/disposition picker in the
 * Patient Administration app — never hardcode these enums in the frontend,
 * they are deployment data.
 */
export interface StayConfig {
  classes: string[];
  /** Class used when a caller admits/registers without naming one. */
  default_class: string;
  /** ADMINISTRATIVE state. Never carries `on_leave` — that is `presences`. */
  statuses: string[];
  /** CLINICAL presence, a separate axis from `statuses`. */
  presences: string[];
  dispositions: string[];
  /** Dispositions a discharge cannot record without saying where the patient
   *  went; the API refuses one of these without a `destination`. */
  dispositions_requiring_destination: string[];
  event_types: string[];
}

/**
 * One launchable agent (app) from the `agents` block of GET /config.
 * Backend populates the catalogue from tricorder-agent's store — the same
 * source its chat pipelines are built from — so the new-session picker is
 * config-driven end to end (a new agent needs no frontend release). Never
 * hardcode an agent list in the frontend.
 */
export interface AgentInfo {
  slug: string;
  name: string;
  /** Single DNS label of the agent's own sub-app host (e.g. protocols →
   *  "alg"); absent for main-app agents. sessionRouting builds
   *  `<subdomain>.<env-host>` session URLs from it, so a new sub-app needs
   *  no frontend release. */
  subdomain?: string;
  /** Default scope tab the agent's non-patient sessions bucket under. The Home
   *  screen combines this with a session's `patient_id` (patient_id != null ⇒
   *  Patienten tab) to place a conversation. Config-sourced (the agent's manifest).
   *  Optional: the backend omits it when a config doesn't set it (omitempty), and
   *  `resolveAgentIdentity` falls back to a neutral default. */
  scope?: 'patient' | 'personal' | 'team';
  /** Agent-identity accent token name — e.g. `primary`, `tone-ok`, `violet`,
   *  `citation`, `neutral-700`, `terracotta`. Resolved to tailwind class fragments
   *  in `agentIdentity.ts`. Optional — neutral fallback if absent. */
  accent?: string;
  /** Agent-identity glyph key — a lucide icon id or a custom registry key
   *  (e.g. `Sparkles`, `CalendarDays`, `venn`). Optional — Bot fallback if absent. */
  icon?: string;
  /** This agent is not open to every user, mirrored from the agent yaml. Says
   *  THAT, never who: this catalogue is unauthenticated and identical for
   *  everybody, so the per-caller answer is `user.launchableAgents`. Absent =
   *  open. */
  restricted?: boolean;
}

/** Language configuration block of GET /config. */
type LanguageConfig = {
  /** Default language code. */
  default: string;
  /** Supported language codes. */
  supported: string[];
  /** Language detection method. */
  detection: string;
};
/** Analytics configuration block of GET /config. */
type AnalyticsConfig = {
  /** Tracked events. */
  events: {
    name: string;
    description: string;
  }[];
  /** Maximum query logs to export in admin analytics endpoint. */
  export_query_log_limit: number;
};

export type AppConfigResponse = {
  /** Config version. */
  version: string;
  language: LanguageConfig;
  profile: ProfileFieldsConfig;
  /** The tenant's activation module, as `Protocol/<slug>`; `""` when the
   *  deployment declares none, which is the shipped default. Nothing in the
   *  client reads it: the onboarding gate that did died with the activation
   *  run. Optional as a block: a /config served by the previous image during
   *  a rollout carries no such key. */
  activation?: { module_ref: string };
  /** Optional: no live consumer today, and an older backend legitimately
   *  omits it. A present-but-malformed block fails the whole /config parse
   *  (schemas.ts) — there is no per-block silent substitution. */
  analytics?: AnalyticsConfig;
  // The backend /config response also carries a `features` block; no frontend
  // code has ever read a flag from it, so it is not typed or parsed here (see
  // schemas.ts note — the backend block is kept for stale bundles).
  // `branding` is OPTIONAL here even though the backend ConfigResponse marks
  // it required. The canonical source is `config/app.yaml` `branding.name`
  // (served via GET /config); when config genuinely has no backend to hit,
  // there is no fallback config to fall back to (see api/endpoints/config.ts)
  // — the app surfaces a visible ConfigErrorScreen instead, and only the DEV
  // preview harnesses (preview/previewConfig.ts) synthesize a config, which
  // omits branding so the single i18n fallback (`app.name`) drives display
  // there. Resolution lives in useBrandName().
  branding?: BrandingConfig;
  /** Optional: query-config.ts's getStaleConfig() already reads
   *  `config?.frontend_cache ?? DEFAULT_FRONTEND_CACHE` — the legitimate
   *  client-side default for an older backend that omits this block. */
  frontend_cache?: FrontendCacheConfig;
  /** Optional: no live consumer today; the server still enforces these
   *  limits regardless of what the frontend does with them. */
  limits?: LimitsConfig;
  knowledge_base?: {
    base_url?: string;
    documents?: unknown[];
    categories?: KnowledgeBaseCategory[];
  };
  patients?: PatientsConfig;
  /** Config-driven stay vocabulary — see StayConfig. REQUIRED: the backend
   *  always sends it, and making it optional turns the `encounter` → `stay`
   *  rename into a silently empty picker instead of a visible error. */
  stay: StayConfig;
  /** Launchable agent catalogue (see AgentInfo). Optional: older backends and
   *  deployments without the chat subsystem omit it — the picker degrades to
   *  the default new-session action only. */
  agents?: AgentInfo[];
  // CUJ-84 org-hierarchy vocabulary (api-service-go OrgConfig). `location_kinds`
  // is the allow-list the Organisation console renders as a kind picker;
  // `known_resource_types` is the set of FGA object types a role grant may
  // target. Both config-driven — the UI must read them here, never hardcode.
  org?: {
    location_kinds: string[];
    /** Hard cap the org list endpoints clamp `limit` to — the orgApi full-set
     *  walkers page at exactly this cap. Optional: older backends omit it. */
    max_page_size?: number;
    // Optional: model-derived + best-effort on the backend (omitted on a cold
    // ACL read). Mirrors the zod schema — must not be required or one missing
    // admin-only field rejects all of /config.
    known_resource_types?: string[];
    // Onboarding self-assignment guardrail (CUJ-84). `role_label` is the labels
    // key marking a role self-selectable during onboarding; the Organisation
    // console reads it so the admin toggle and the backend enforcement share one
    // config-sourced key. Onboarding reads the whole block to filter the
    // respective GET /roles and GET /locations endpoints by the SAME keys the
    // backend enforces on write. Optional — mirrors the zod schema.
    self_assign?: {
      enabled: boolean;
      role_label: string;
      /** Location kinds a user may self-assign to during onboarding. */
      location_kinds: string[];
    };
  };
  /**
   * The staffing vocabulary (api-service-go `staffing`) — the team kinds and
   * the grade ladder. The SHIFT-TYPE catalogue is not here: it is the roster
   * plan category's declaration in the planning service, so the
   * codes a ward works have one home and it is the planning service's.
   */
  staffing?: StaffingConfig;
};

/** One nursing qualification a team member holds and coverage is counted against. */
export type QualificationConfig = {
  code: string;
  label: Record<string, string>;
};

/** One staffing-unit kind a team may take. */
export type TeamKindConfig = {
  code: string;
  label: Record<string, string>;
};

/**
 * The `staffing` block from GET /config: the staffing-unit vocabulary and the
 * grade ladder.
 *
 * A team is a PARTY and a qualification is something a person HOLDS, so both
 * are this platform's vocabulary.
 */
export type StaffingConfig = {
  /** The skill-mix vocabulary a membership's `qualification` comes from. */
  qualifications?: QualificationConfig[];
  /** The staffing-unit vocabulary a team's `kind` comes from. */
  team_kinds?: TeamKindConfig[];
  /** The location kinds a team may staff. */
  staffed_location_kinds?: string[];
  /** Maps a profile role (users.role) to the qualification its coverage counts
   *  against. A role with no entry carries no qualification — an honest
   *  absence, which the board reports rather than guessing a grade. */
  role_qualifications?: Record<string, string>;
};

/** API error envelope (not a Pydantic model in backend). */
export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// -- Health status literal types --
export type HealthStatus = HealthResponse["status"];
export type ServiceStatus = ServiceHealth["status"];

// ============================================================================
// Bookmark Enrichment Types (frontend-only, not in backend response schema)
// ============================================================================

/**
 * Enrichment failure reason values.
 *
 * Transient (retryable): pubmed_timeout, pubmed_api_error, llm_timeout, llm_rate_limit, llm_api_error
 * Permanent (non-retryable): no_enrichment_needed, no_results_found, invalid_content, unknown
 */
export type FailureReason =
  | "pubmed_timeout"
  | "pubmed_api_error"
  | "llm_timeout"
  | "llm_rate_limit"
  | "llm_api_error"
  | "no_enrichment_needed"
  | "no_results_found"
  | "invalid_content"
  | "enrichment_error"
  | "unknown";

/** Set of transient failure reasons that the backend will automatically retry. */
export const TRANSIENT_FAILURE_REASONS = new Set<FailureReason>([
  "pubmed_timeout",
  "pubmed_api_error",
  "llm_timeout",
  "llm_rate_limit",
  "llm_api_error",
]);

/** Metadata about enrichment retries. */
export interface EnrichmentMetadata {
  attempt_count: number;
  max_attempts: number;
  started_at?: string | null;
  completed_at?: string | null;
  duration_ms?: number | null;
  failure_reason?: FailureReason | null;
  last_error?: string | null;
  failed_phase?: "orchestrator" | "research" | "synthesis" | null;
  retry_after?: string | null;
  pubmed_searched?: boolean;
  sai_searched?: boolean;
  queries_used?: string[];
}

// ============================================================================
// Frontend Domain Types (camelCase for internal use)
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: string | null;
  yearsExperience: number | null;
  specialisation: string | null;
  institution?: string | null;
  /** Display unit/ward (free text). For org-managed units this mirrors the
   *  selected location's name; the authoritative org link is the location id. */
  unit?: string | null;
  /** House-standard References (source of truth for where the user works). */
  roleRef?: Reference | null;
  locationRef?: Reference | null;
  /** The deployment's org-tree root; present only when the deployment has
   *  one unambiguous org root. See UserResponse.organisation_ref. */
  organisationRef?: Reference | null;
  language: "de" | "fr" | "en";
  twinName: string;
  /** The user's own summary of their practice; null when they have written none. */
  narrative: string | null;

  /** True when the user holds the platform-admin role (read + write). Defaults false. */
  isPlatformAdmin: boolean;
  /**
   * READ-ONLY admin tier: full admins, delegated read-only admins, and every
   * new user during their 30-day trial. Gates the Admin section + aggregate
   * tabs; the raw cross-user tabs and permission management stay gated on
   * `isPlatformAdmin`. Defaults false.
   */
  adminView: boolean;
  /**
   * When the 30-day new-user admin-view trial lapses (ISO-8601). Present only
   * while the user is inside the trial; otherwise null.
   */
  adminViewTrialExpiresAt: string | null;
  /** Delegable approval authority — gates the Approvals admin area. */
  canApprove: boolean;
  /** Admin areas (endpoint instance ids) this caller may open. Defaults []. */
  adminEndpoints: string[];
  /**
   * Every agent this caller may open a session with, or `undefined` when we
   * have not been told yet.
   *
   * The two states are NOT the same and the launcher renders them differently:
   * `[]` is an answer ("you may open nothing"), `undefined` is the absence of
   * one — a cached profile from before this field existed, or a `/me` still in
   * flight against a cold api-service. Collapsing them to `[]` would show a
   * nurse an empty Home for the length of a cold start, so a caller that does
   * not know falls back to the agents config itself marks unrestricted.
   */
  launchableAgents?: string[];
  /**
   * The caller's direct (admin-granted) ward memberships. Same treatment as
   * `launchableAgents` above (deliberately NO default anywhere, including in
   * toUser's full mapping): `[]` is an answer ("no memberships"), `undefined`
   * is the absence of one (older backend, response still in flight). A
   * caller that does not know falls back to whatever is correct for it
   * locally — never to `[]`, which would read as a resolved "none".
   */
  locationMemberships?: LocationMembership[];
  createdAt: string;
  updatedAt: string;
}

/**
 * The caller-capability half of a user: what THIS caller may reach. Carried by
 * GET /me and the auth envelope — every other endpoint that returns a user
 * (PUT /me among them) serialises the profile without it.
 *
 * Present keys only, so `updateUser`'s spread cannot overwrite a resolved
 * capability with a default from a response that never carried it. That bug
 * shipped once already in a milder form: saving a profile edit dropped the
 * caller's admin endpoints until the next boot reconcile, and it was invisible
 * because the agent launcher did not depend on any of these fields. It does now.
 */
const CALLER_CAPABILITY_KEYS = [
  'isPlatformAdmin',
  'adminView',
  'adminViewTrialExpiresAt',
  'canApprove',
  'adminEndpoints',
  'launchableAgents',
  'locationMemberships',
] as const satisfies readonly (keyof User)[];

export function callerCapabilitiesFrom(response: UserResponse): Partial<User> {
  const caps: Partial<User> = {};
  if (response.is_platform_admin !== undefined) caps.isPlatformAdmin = response.is_platform_admin;
  if (response.admin_view !== undefined) caps.adminView = response.admin_view;
  if (response.can_approve !== undefined) caps.canApprove = response.can_approve;
  if (response.admin_endpoints !== undefined) caps.adminEndpoints = response.admin_endpoints;
  if (response.launchable_agents !== undefined) caps.launchableAgents = response.launchable_agents;
  if (response.location_memberships !== undefined) caps.locationMemberships = response.location_memberships;
  if (response.admin_view_trial_expires_at !== undefined) {
    caps.adminViewTrialExpiresAt = response.admin_view_trial_expires_at;
  }
  return caps;
}

/**
 * Convert UserResponse (snake_case) to User (camelCase).
 *
 * Use this for a WHOLE user (setAuth, the /me reconcile). To merge a response
 * that carries only the profile half — PUT /me — use `toUserPatch`, which
 * leaves the caller's capabilities alone.
 */
export function toUser(response: UserResponse): User {
  return {
    id: response.id,
    email: response.email,
    name: response.name ?? null,
    role: response.role ?? null,
    yearsExperience: response.years_experience ?? null,
    specialisation: response.specialisation ?? null,
    institution: response.institution,
    unit: response.unit ?? null,
    roleRef: response.role_ref ?? null,
    locationRef: response.location_ref ?? null,
    organisationRef: response.organisation_ref ?? null,
    language: response.language as "de" | "fr" | "en",
    twinName: response.twin_name,
    // Absent (the previous image during a rollout) and null (she has written
    // none) both mean "no narrative to show" in the app's own model, which is
    // why the wire distinction stops here rather than being carried inward.
    narrative: response.narrative ?? null,
    isPlatformAdmin: response.is_platform_admin ?? false,
    adminView: response.admin_view ?? false,
    adminViewTrialExpiresAt: response.admin_view_trial_expires_at ?? null,
    canApprove: response.can_approve ?? false,
    adminEndpoints: response.admin_endpoints ?? [],
    launchableAgents: response.launchable_agents,
    locationMemberships: response.location_memberships,
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

/**
 * A user PATCH for `updateUser`: the profile fields a response actually
 * carried, and the caller capabilities only if they were on the wire.
 *
 * `updateUser` spreads what it is given over the stored user, so passing a full
 * `toUser(...)` of a profile-only response writes that mapping's DEFAULTS —
 * `adminEndpoints: []`, `launchableAgents: undefined` — over answers GET /me
 * had already resolved. This is the shape those call sites want.
 */
export function toUserPatch(response: UserResponse): Partial<User> {
  const patch: Partial<User> = { ...toUser(response) };
  for (const key of CALLER_CAPABILITY_KEYS) delete patch[key];
  return { ...patch, ...callerCapabilitiesFrom(response) };
}

/**
 * Build a User from an AuthResponse, merging the top-level fields (siblings of
 * `user` in the AuthResponse envelope) onto the base toUser mapping. The nested
 * UserResponse does NOT carry them on the wire — the backend serialises them at
 * the AuthResponse top level — so a mapping that read only `r.user` would drop
 * every one of them.
 *
 * Where the sign-in LANDS is not among them, and deliberately. No course is
 * special, so the envelope names none: a client that wants to know whether the
 * account has an unfinished first course asks the sessions list for its own
 * training runs, the same way it would ask about any other course.
 *
 * All four auth hooks (useLogin, useRegister, useVerifyEmail, useOAuthCallback)
 * use this instead of repeating the merge inline.
 */
export function userFromAuthResponse(r: AuthResponse): User {
  return {
    ...toUser(r.user),
    adminView: r.admin_view ?? false,
    adminViewTrialExpiresAt: r.admin_view_trial_expires_at ?? null,
  };
}

// ============================================================================
// SSE Streaming Event Types (for real-time chat responses)
// ============================================================================

/** Emitted when streaming begins. Provides message IDs for optimistic state. */
export interface StreamingMessageStartEvent {
  event: "message_start";
  user_message_id: string;
  assistant_message_id: string;
}

/** Emitted for each token chunk from the LLM. */
export interface StreamingContentDeltaEvent {
  event: "content_delta";
  delta: string;
}

/** Emitted when a tool execution begins. */
export interface StreamingToolUseStartEvent {
  event: "tool_use_start";
  tool_id: string;
  tool_name: string;
}

/** Emitted when a tool execution completes. */
export interface StreamingToolUseEndEvent {
  event: "tool_use_end";
  tool_name: string;
}

/** Emitted with the final citations array. */
export interface StreamingCitationsEvent {
  event: "citations";
  citations: CitationResponse[];
}

/** Emitted with UI content blocks. */
export interface StreamingBlocksEvent {
  event: "blocks";
  blocks: ContentBlock[];
}

/** Emitted when streaming completes successfully. */
export interface StreamingMessageCompleteEvent {
  event: "message_complete";
  user_message: MessageResponse;
  assistant_message: MessageResponse;
  degraded_services?: string[] | null;
}

/** Emitted when an error occurs during streaming. */
export interface StreamingErrorEvent {
  event: "error";
  error_code: string;
  error_message: string;
  partial_content?: string | null;
  partial_assistant_message?: MessageResponse | null;
}

/** Emitted periodically during silence to prevent client timeout detection. */
export interface StreamingKeepaliveEvent {
  event: "keepalive";
  data: string;
}

/** Union type for all streaming events. */
export type StreamingEvent =
  | StreamingMessageStartEvent
  | StreamingContentDeltaEvent
  | StreamingToolUseStartEvent
  | StreamingToolUseEndEvent
  | StreamingCitationsEvent
  | StreamingBlocksEvent
  | StreamingMessageCompleteEvent
  | StreamingErrorEvent
  | StreamingKeepaliveEvent
  | ElicitationPromptEvent;

// -- Agentic UI Fabric (P0) --
// Manual mirrors of tricorder.fabric.v1 protojson (camelCase), same
// convention as the mcpui UIAction interfaces above.

export interface DirectiveWire {
  surfaceId?: string;
  render?: { block?: Record<string, unknown> };
  navigate?: { targetGuideId?: string };
  spotlight?: { guideIds?: string[]; coachMark?: string };
  prompt?: { text?: string };
  notify?: { message?: string };
}

export interface DirectiveEvent {
  sessionId: string;
  directive: DirectiveWire;
}

/** Host -> agent feedback, POSTed via /sessions/{id}/ui_actions surfaceEvent. */
export interface SurfaceEventWire {
  surfaceId: string;
  surfaceChanged?: { toSurfaceId?: string };
  stepDone?: { guideId?: string };
  confirmed?: { guideId?: string };
  dismissed?: { guideId?: string };
}
